import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalizationStore } from '@/store/localizationStore';
import { getSavedJobsList, getSavedJobsCount, unsaveJob } from '@/services/api/homeApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { PageLayout } from '@/components/layout/PageLayout';
import { HiredRequisitionsProvider } from '@/features/onboarding/entry';
import { HomeJobListItem } from '@/features/home/components/HomeJobListItem';
import { ViewToggle } from './components/ViewToggle';
import { JOB_SHARE_REF } from './utils/jobOrigin';
import { ROUTES } from '@/app/router/routes';
import type { JobEntity } from '@/types/job.types';
import styles from './SavedJobsPage.module.css';

/**
 * SavedJobsPage — /savedJobs, a public route reachable only by URL.
 * Mirrors Flutter SavedJobs + SavedJobsBloc (presentation/web/saved_jobs/saved_jobs.dart,
 * application/saved_jobs/saved_jobs_bloc.dart, infrastructure/saved_jobs/i_saved_jobs_repository.dart):
 *   - signed out, or when the request fails → "No Records"
 *   - unpaged GET v1/candidate/_savedJobs, reloaded on every language change
 *   - card / list toggle (JobCard grid or list) with Flutter's job cards: share, posted date,
 *     Apply / Easy Apply (both go to /profile, as in Flutter)
 *   - the title opens /jobs/{entity.id}?ref=job-share-internal-link in the same tab
 *
 * Kept from React: unsaving works (Flutter's onUnSave is empty) — the job leaves the list and
 * the header badge is refreshed.
 */

export function SavedJobsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const config = useTenantStore((s) => s.config);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);
  const enableEasyApply = !!(config as Record<string, unknown> | null)?.enableEasyApply;

  const [jobs, setJobs] = useState<JobEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFailed, setIsFailed] = useState(false);
  const [isCardView, setIsCardView] = useState(true);

  // Flutter _GetSavedJobs (on init and on languageStream)
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setJobs([]);
      setIsFailed(true);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setIsFailed(false);
    getSavedJobsList()
      .then((res) => {
        // Every job here is saved — the bookmark shows filled and unsaves
        if (!cancelled) setJobs(res.entities.map((e) => ({ ...e, isSavedJob: true })));
      })
      .catch(() => {
        if (!cancelled) setIsFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, currentLanguage]);

  const handleUnsave = useCallback(async (job: JobEntity) => {
    if (!job.displayId) return;
    setJobs((prev) =>
      prev.map((j) => (j.displayId === job.displayId ? { ...j, isLoadingForSaveJobButton: true } : j)),
    );
    try {
      if (!(await unsaveJob(job.displayId))) throw new Error('Unsave failed');
      setJobs((prev) => prev.filter((j) => j.displayId !== job.displayId));
    } catch {
      setJobs((prev) =>
        prev.map((j) => (j.displayId === job.displayId ? { ...j, isLoadingForSaveJobButton: false } : j)),
      );
    }
    // Header badge — Flutter AppStateNotifier.changeJobCount
    getSavedJobsCount().catch(() => {});
  }, []);

  // Flutter onTapTitle: same tab, by entity.id, analyticsClass saved_jobs_page
  const handleOpenJob = useCallback(
    (job: JobEntity) => {
      void analyticsService.logScreenView({ screenName: ROUTES.JOBS, screenClass: 'saved_jobs_page' });
      navigate(`${ROUTES.JOBS}/${job.id ?? ''}?ref=${JOB_SHARE_REF.INTERNAL}`);
    },
    [navigate],
  );

  // Flutter onApply: navigateTo(profile, arguments: ProfileInput(tabIndex: 4)) for either button
  const handleApply = useCallback(() => {
    void analyticsService.logScreenView({ screenName: ROUTES.PROFILE });
    navigate(ROUTES.PROFILE);
  }, [navigate]);

  if (isLoading) {
    return (
      <PageLayout>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
        </div>
      </PageLayout>
    );
  }

  if (isFailed) {
    return (
      <PageLayout>
        <div className={styles.emptyContainer}>
          <p className={styles.emptyText}>{t('No Records')}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Candidate onboarding (React only, FLUTTER_DEVIATIONS §5): the jobs the candidate is hired for, loaded once for the
          Onboarding In Progress buttons on the cards and the detail panel; nothing in prod */}
      <HiredRequisitionsProvider>
        <div className={styles.container}>
          <div className={styles.toolbar}>
            <ViewToggle isCardView={isCardView} onToggle={() => setIsCardView((v) => !v)} />
          </div>

          <div className={isCardView ? styles.cardGrid : styles.listView}>
            {jobs.map((job, index) => (
              <HomeJobListItem
                key={job.id ?? job.displayId ?? index}
                entity={job}
                isSelected={false}
                onSelect={() => handleOpenJob(job)}
                // Flutter's onSave here is empty too; every listed job is saved
                onSave={() => {}}
                onUnsave={() => void handleUnsave(job)}
                enableEasyApply={enableEasyApply}
                onApply={handleApply}
                alwaysShowActions
                titleLink={{
                  href: `${ROUTES.JOBS}/${job.id ?? ''}?ref=${JOB_SHARE_REF.INTERNAL}`,
                  newTab: false,
                  onOpen: () => handleOpenJob(job),
                }}
              />
            ))}
          </div>
        </div>
      </HiredRequisitionsProvider>
    </PageLayout>
  );
}
